import type { Alert } from "@/types/models";

// Synthesis of the scattered per-flock alerts into a ranked "which flock to act
// on" list. The alert engine already computes the per-flock rules (conditions,
// dedup, severity); this groups those alerts by flock and ranks the flocks so
// the owner gets a decision, not a flat list. Pure + unit-tested, mirroring
// flockStage.ts. See docs/architecture/alerts.md.

// Compact chips for the reason line, keyed by the STABLE alert rule_id. Adding a
// new flock-scoped rule means adding its label here — see the "Chip-label
// contract" in docs/architecture/alerts.md. An unmapped rule falls back to its
// full alert title (a sentence — a visible bug-catcher, not a design).
const RULE_SHORT_LABEL: Record<string, string> = {
  flock_spent: "spent",
  unviable: "eggs can't cover costs",
  hdp_below_breakeven: "below break-even",
  hdp_below_target: "HDP below target",
  cost_per_egg_above_target: "cost/egg over target",
  fcr_above_target: "FCR over target",
  fcr_unmeasurable: "FCR unmeasured",
  mortality_above_target: "mortality over target",
  mortality_above_standard: "mortality over standard",
  daily_entry_missing: "no entry yesterday",
  mortality_rate_high: "deaths spiked",
  production_drop: "production fell",
  feed_intake_drop: "feed intake down",
  light_hours_below_programme: "light hours short",
  vaccination_overdue: "vaccination overdue",
  body_weight_below_standard: "under body-weight standard",
};

// A flock usually trips several rules at once; the owner needs ONE next move,
// not a menu. RULE_ACTION maps each rule to the action it implies, and
// ACTION_PRIORITY picks the single most decisive one (most-decisive first).
// This order is DELIBERATELY decoupled from alert severity (`flock_spent` is a
// warning but ranks #1). Rationale:
//   - `flock_spent` wins outright — an end-of-life flock is culled regardless of
//     its economics/health, and the mortality rule it may also trip is
//     cumulative-vs-standard (age-expected), not an acute-spike detector.
//   - mortality is next — an active health signal (disease/heat, possibly
//     contagious) is the most time-critical thing on a still-productive flock,
//     and is often the CAUSE of the poor economics below it. It outranks the
//     economics rules even though `unviable` is the only *critical* severity.
//   - then the economics rules, worst-first (unviable > break-even > cost > feed > goal).
// An unmapped/future rule is skipped (falls through to the next mapped rule).
const ACTION_PRIORITY = [
  // The ACUTE rules come FIRST, above everything including a spent flock.
  //
  // They are the only rules on this list about something that happened
  // YESTERDAY. Everything below is a standing condition — a flock past its
  // economic life, costs over target, production under a curve — and all of
  // those are still true tomorrow. A mortality spike is not: it is either
  // caught today or it is a week of losses, and the shed that needs walking
  // into this morning outranks the one that needs a decision this month.
  "mortality_rate_high",
  "production_drop",
  "feed_intake_drop",
  "light_hours_below_programme",
  // And the rule that makes those three trustworthy. All of them read what a
  // supervisor wrote down, so a shed nobody logged is silent on every one —
  // and silence on this dashboard reads as a healthy shed. It sits below them
  // because a spike that WAS recorded outranks a book that wasn't filled in,
  // and above everything else because until the entry arrives the owner has no
  // idea which of the rules below apply.
  "daily_entry_missing",
  "flock_spent",
  // Vaccination outranks everything still alive. It is the only rule on this
  // list with a CLOSING WINDOW — a dose not given this week cannot be given
  // later, whereas feed, costs and production can all be corrected next month.
  "vaccination_overdue",
  "mortality_above_standard",
  "mortality_above_target",
  "unviable",
  "hdp_below_breakeven",
  // Body weight sits above the cost rules and below the acute ones: it is an
  // EARLY signal, so it is not urgent today, but it is the cause of the
  // production shortfall those rules will report in six weeks' time.
  "body_weight_below_standard",
  "cost_per_egg_above_target",
  "fcr_above_target",
  "hdp_below_target",
  // LAST on purpose. "Weigh a tray of eggs" is housekeeping, not a decision
  // about the flock — it must never displace a real problem in the single
  // next-move slot. It only fires for a farm that set an FCR ceiling and so
  // asked to be told, and only when the ceiling cannot be checked at all.
  "fcr_unmeasurable",
];

const RULE_ACTION: Record<string, string> = {
  flock_spent: "Cull or replace this flock",
  unviable: "Losing money per egg — review costs or cull",
  mortality_above_standard: "Investigate mortality — check for disease or heat stress",
  daily_entry_missing: "Ask the shed supervisor for yesterday's mortality, feed and eggs",
  mortality_rate_high: "Deaths above the daily limit — check for disease, heat or water failure",
  production_drop: "Production fell off its own average — inspect the shed today",
  feed_intake_drop: "Birds are eating less — check feed, water and temperature",
  light_hours_below_programme:
    "Check the timer — shortening a laying flock's day stops it laying",
  mortality_above_target: "Investigate mortality — check for disease or heat stress",
  hdp_below_breakeven: "Below break-even — lift production or cut costs",
  cost_per_egg_above_target: "Costs above target — review feed and inputs",
  fcr_above_target: "Feed efficiency below target — review the feed formula",
  fcr_unmeasurable: "Weigh a tray of eggs — your FCR ceiling can't be checked without it",
  hdp_below_target: "Production below target — investigate the drop",
  vaccination_overdue: "Give the overdue dose and record it",
  body_weight_below_standard: "Under the growth curve — check feed intake and space",
};

// Every flock-scoped rule, for the contract test in `flockPriority.test.ts` —
// never hand-copied. That array used to be typed out in the test with a "keep
// in sync" comment and had drifted five rules behind, green right through the
// slice that shipped two rules with no action at all.
//
// This is the manifest's flock set, not merely "whatever RULE_ACTION holds":
// `check_alert_rules.sh` checks BOTH directions — every flock rule in
// `scripts/guards/alert_rules_manifest.txt` has a RULE_ACTION entry, and every
// RULE_ACTION key is a flock rule in that manifest — so the two are equal by
// construction, and `check_alert_rules_selftest.sh` proves that check can
// fail. Without the reverse direction a rule missing from the map would simply
// contribute zero test cases, which is the same hole in a new shape.
export const FLOCK_RULE_IDS = Object.keys(RULE_ACTION);

export interface FlockPriority {
  flock_id: number;
  flock_name: string;
  score: number;
  criticalCount: number;
  warningCount: number;
  alerts: Alert[]; // this flock's alerts, critical-first (stable within severity)
  reasons: string[]; // one short chip per alert, in the same order
  action?: string; // the single recommended next move (from the top-priority rule)
}

const CRITICAL_WEIGHT = 100;
const WARNING_WEIGHT = 10;

function reasonFor(a: Alert): string {
  return RULE_SHORT_LABEL[a.rule_id] ?? a.title;
}

// The single recommended action for a flock: the action of its highest-priority
// rule. Requires both `rules.has(r)` AND a RULE_ACTION mapping, so a rule listed
// in ACTION_PRIORITY but (mistakenly) missing from RULE_ACTION is skipped rather
// than shadowing a lower-priority mapped rule with a blank action. Undefined if
// none of the flock's rules map (a future rule) — the reasons still render.
function recommendedAction(alerts: Alert[]): string | undefined {
  const rules = new Set(alerts.map((a) => a.rule_id));
  const top = ACTION_PRIORITY.find((r) => rules.has(r) && RULE_ACTION[r]);
  return top ? RULE_ACTION[top] : undefined;
}

/**
 * rankFlocksToActOn groups the (already-fetched) alerts by flock and returns the
 * worst `limit` flocks, most-in-need-of-action first (or ALL of them when `limit`
 * is omitted — the dashboard shows every flock needing action, the comparison
 * page caps at a top-N summary). Operational alerts (low_stock / overdue_*) have
 * no flock_id and are ignored.
 *
 * Score = criticalCount*100 + warningCount*10, so a single critical outranks any
 * number of warnings and issue-count breaks warning-only ties. Ranking is fully
 * deterministic (score → criticals → total count → flock_id).
 */
export function rankFlocksToActOn(alerts: Alert[], limit?: number): FlockPriority[] {
  const byFlock = new Map<number, { name: string; alerts: Alert[] }>();
  for (const a of alerts) {
    if (a.flock_id == null) continue; // operational (farm-level) alert — not flock-scoped
    const g = byFlock.get(a.flock_id);
    if (g) {
      g.alerts.push(a);
      // Prefer any non-empty name across the group — don't get stuck with a
      // blank/absent name from whichever alert happened to be first.
      if (!g.name && a.flock_name) g.name = a.flock_name;
    } else {
      // `||` (not `??`) so an empty-string name also falls through to the id.
      byFlock.set(a.flock_id, { name: a.flock_name || "", alerts: [a] });
    }
  }

  const ranked: FlockPriority[] = [];
  for (const [flock_id, g] of byFlock) {
    // Critical-first, stable within a severity (preserves the rule order the
    // backend emitted, i.e. severity dedup already applied).
    const ordered = [...g.alerts].sort(
      (x, y) => severityRank(x.severity) - severityRank(y.severity),
    );
    const criticalCount = ordered.filter((a) => a.severity === "critical").length;
    const warningCount = ordered.length - criticalCount;
    ranked.push({
      flock_id,
      flock_name: g.name || `Flock #${flock_id}`,
      score: criticalCount * CRITICAL_WEIGHT + warningCount * WARNING_WEIGHT,
      criticalCount,
      warningCount,
      alerts: ordered,
      reasons: ordered.map(reasonFor),
      action: recommendedAction(ordered),
    });
  }

  ranked.sort(
    (a, b) =>
      b.score - a.score ||
      b.criticalCount - a.criticalCount ||
      b.alerts.length - a.alerts.length ||
      a.flock_id - b.flock_id,
  );
  return limit === undefined ? ranked : ranked.slice(0, limit);
}

function severityRank(s: Alert["severity"]): number {
  return s === "critical" ? 0 : 1;
}

// One snooze command per alert. Muting a flock from the card = snoozing every
// alert it's currently firing, so the whole flock drops off the dashboard.
export interface FlockSnoozePayload {
  rule_id: string;
  flock_id?: number;
  label: string;
  days?: number;
}

// flockSnoozePayloads turns "mute this flock for N days" into the per-alert
// snooze calls to send (label snapshots what was muted, for the "Snoozed" list).
export function flockSnoozePayloads(flock: FlockPriority, days?: number): FlockSnoozePayload[] {
  return flock.alerts.map((a) => ({
    rule_id: a.rule_id,
    flock_id: a.flock_id,
    label: `${a.flock_name ? `${a.flock_name}: ` : ""}${a.title}`,
    days,
  }));
}
